import {
  validateBoolean,
  validateEmail,
  validateInteger,
  validateNumber,
  validatePhone,
  validatePostalCode,
  validateString,
  validateTimestamp,
  validateUrl,
} from "../validators";
import { CSVFieldType, CSVValidationError, FieldSchema } from "./validate-csv";

/**
 * Validate a field value based on its type
 * Uses imported validators from @tellescope/utils/validators
 */
export const validateFieldValue = <T = unknown>(
  value: string,
  field: string,
  schema: FieldSchema<T>,
  rowNumber: number
): { value: unknown; error?: CSVValidationError } | null => {
  // Handle custom validators
  if (typeof schema.type === "function") {
    return schema.type(value, {}, rowNumber);
  }

  // Use validators directly and add row number to errors
  let result: { value: unknown; error?: CSVValidationError } | null = null;

  switch (schema.type) {
    case CSVFieldType.STRING: {
      const validatorResult = validateString(value, {
        field,
        minLength: schema.minLength,
        maxLength: schema.maxLength,
        pattern: schema.pattern,
      });
      result = validatorResult.error
        ? { value: validatorResult.value, error: { ...validatorResult.error, row: rowNumber } }
        : { value: validatorResult.value };
      break;
    }
    case CSVFieldType.NUMBER: {
      const validatorResult = validateNumber(value, {
        field,
        min: schema.min,
        max: schema.max,
      });
      if (!validatorResult) {
        return {
          value: null,
          error: {
            row: rowNumber,
            field,
            message: `Invalid number value for "${field}": ${value}`,
            severity: "warning",
          },
        };
      }
      result = validatorResult.error
        ? { value: validatorResult.value, error: { ...validatorResult.error, row: rowNumber } }
        : { value: validatorResult.value };
      break;
    }
    case CSVFieldType.INTEGER: {
      const validatorResult = validateInteger(value, {
        field,
        min: schema.min,
        max: schema.max,
      });
      if (!validatorResult) {
        return {
          value: null,
          error: {
            row: rowNumber,
            field,
            message: `Invalid integer value for "${field}": ${value}`,
            severity: "warning",
          },
        };
      }
      result = validatorResult.error
        ? { value: validatorResult.value, error: { ...validatorResult.error, row: rowNumber } }
        : { value: validatorResult.value };
      break;
    }
    case CSVFieldType.TIMESTAMP: {
      const validatorResult = validateTimestamp(value, { field });
      if (!validatorResult || validatorResult.error) {
        return {
          value: null,
          error: {
            row: rowNumber,
            field,
            message: `Invalid date format for "${field}": ${value}. Expected ISO 8601 or MM/DD/YYYY`,
            severity: "warning",
          },
        };
      }
      result = { value: validatorResult.value };
      break;
    }
    case CSVFieldType.BOOLEAN: {
      const validatorResult = validateBoolean(value, { field });
      if (!validatorResult || validatorResult.error) {
        return {
          value: null,
          error: {
            row: rowNumber,
            field,
            message: `Invalid boolean value for "${field}": ${value}. Expected true/false, yes/no, 1/0, on/off`,
            severity: "warning",
          },
        };
      }
      result = { value: validatorResult.value };
      break;
    }
    case CSVFieldType.EMAIL: {
      const validatorResult = validateEmail(value, {
        field,
        pattern: schema.pattern,
      });
      if (!validatorResult) {
        return {
          value: null,
          error: {
            row: rowNumber,
            field,
            message: `Invalid email format for "${field}": ${value}`,
            severity: "warning",
          },
        };
      }
      result = validatorResult.error
        ? { value: validatorResult.value, error: { ...validatorResult.error, row: rowNumber } }
        : { value: validatorResult.value };
      break;
    }
    case CSVFieldType.URL: {
      const validatorResult = validateUrl(value, { field });
      if (!validatorResult || validatorResult.error) {
        return {
          value: null,
          error: {
            row: rowNumber,
            field,
            message: `Invalid URL format for "${field}": ${value}`,
            severity: "warning",
          },
        };
      }
      result = { value: validatorResult.value };
      break;
    }
    case CSVFieldType.PHONE: {
      const validatorResult = validatePhone(value, {
        field,
        pattern: schema.pattern,
      });
      if (!validatorResult) {
        return {
          value: null,
          error: {
            row: rowNumber,
            field,
            message: `Invalid phone number format for "${field}": ${value}`,
            severity: "warning",
          },
        };
      }
      result = validatorResult.error
        ? { value: validatorResult.value, error: { ...validatorResult.error, row: rowNumber } }
        : { value: validatorResult.value };
      break;
    }
    case CSVFieldType.POSTAL_CODE: {
      const validatorResult = validatePostalCode(value, { field });
      if (!validatorResult || validatorResult.error) {
        return {
          value: null,
          error: {
            row: rowNumber,
            field,
            message: `Invalid postal code format for "${field}": ${value}`,
            severity: "warning",
          },
        };
      }
      result = { value: validatorResult.value };
      break;
    }
    default:
      result = { value };
  }

  // Check allowed values if specified
  if (result && schema.allowedValues && !schema.allowedValues.includes(result.value)) {
    return {
      value: null,
      error: {
        row: rowNumber,
        field,
        message: `"${field}" must be one of: ${schema.allowedValues.join(", ")}. Got: ${value}`,
        severity: "warning",
      },
    };
  }

  return result;
};
